import { useEffect, useMemo, useState } from "react";
import {
  HiCheck,
  HiExclamation,
  HiOutlineDocumentText,
  HiShieldCheck,
  HiX,
} from "react-icons/hi";
import { Spinner } from "flowbite-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useTranslation,
  useAuthentication,
  daysUntilExpiry,
  isSubscriptionExpiryUrgent,
  isSubscriptionExpiryVisible,
  subscriptionExpiryTone,
} from "@repo/core";
import {
  useProducts,
  useActiveSubscription,
  FACTOR_INVESTING_BASIC_PERMISSION,
  type ProductWithPurchaseStatus,
  type StripePrice,
} from "@repo/api";
import { useStripeCheckout } from "../../hooks/useStripeCheckout";

/** Billing intervals rendered as cards, in display order. */
const INTERVAL_ORDER = ["year", "month"];

/** Which set of left-column copy the panel speaks with. The four states say
 * the same four things in different tenses - "you have this" / "keep this" /
 * "get this back" - so the wording is selected as one block rather than
 * per-paragraph. Keys are spelled out so each stays greppable from the locale
 * files. */
type CopyVariant = "active" | "noSubscription" | "expired" | "ending";

interface LeftColumnCopy {
  title: string;
  intro: string;
  benefitsHeading: string;
  benefits: readonly string[];
}

const LEFT_COLUMN_COPY: Record<CopyVariant, LeftColumnCopy> = {
  // Paid up and renewing on its own.
  active: {
    title: "activeTitle",
    intro: "activeIntro",
    benefitsHeading: "activeContinueHeading",
    benefits: [
      "activeBenefit1",
      "activeBenefit2",
      "activeBenefit3",
      "activeBenefit4",
    ],
  },
  // Access already lapsed.
  expired: {
    title: "expiredTitle",
    intro: "expiredIntro",
    benefitsHeading: "expiredContinueHeading",
    benefits: [
      "expiredBenefit1",
      "expiredBenefit2",
      "expiredBenefit3",
      "expiredBenefit4",
    ],
  },
  // Never had a subscription: its own title/intro, but the same forward-looking
  // benefit list as the expiring state.
  noSubscription: {
    title: "noSubscriptionTitle",
    intro: "noSubscriptionIntro",
    benefitsHeading: "continueHeading",
    benefits: ["benefit1", "benefit2", "benefit3", "benefit4"],
  },
  // Still running, but ending and not set to renew.
  ending: {
    title: "title",
    intro: "intro",
    benefitsHeading: "continueHeading",
    benefits: ["benefit1", "benefit2", "benefit3", "benefit4"],
  },
};

interface SubscriptionUpgradePanelProps {
  /** Product permission whose plans are offered. */
  permission?: string;
  /** When provided a close (✕) button is shown — i.e. rendered inside the modal. */
  onClose?: () => void;
}

/** One selectable plan: a product paired with one of its prices. */
interface PlanOption {
  product: ProductWithPurchaseStatus;
  price: StripePrice;
}

/**
 * The two-column subscription-renewal content: value proposition on the left,
 * plan options + checkout on the right. Shared verbatim by
 * {@link SubscriptionUpgradeModal} and the `/subscriptions` page so the two
 * always look identical.
 */
const SubscriptionUpgradePanel = ({
  permission = FACTOR_INVESTING_BASIC_PERMISSION,
  onClose,
}: SubscriptionUpgradePanelProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuthentication();
  const { products, isLoading } = useProducts();
  const { data: subscription } = useActiveSubscription(permission);
  const {
    createCheckoutSession,
    createCustomerPortalSession,
    loading: checkoutLoading,
    customerPortalLoading,
  } = useStripeCheckout();

  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);

  // Czech UI -> CZK prices, every other language -> EUR.
  const currency = i18n.language.toLowerCase().startsWith("cs") ? "czk" : "eur";

  // Flatten (product x price) to one recurring option per billing interval, in
  // the language's currency. Strictly limited to products carrying the target
  // `base_permission` — no fallback to other products. `isPurchased` is NOT
  // filtered out: the user already holds this permission and the goal here is to
  // renew that same subscription. The currency filter does fall back, so a
  // catalog missing CZK prices still shows the EUR ones.
  const planOptions = useMemo<PlanOption[]>(() => {
    const forPermission = products.filter(
      (p) => p.metadata?.base_permission === permission
    );

    const all = forPermission.flatMap((product) =>
      product.prices.map((price) => ({ product, price }))
    );
    const inCurrency = all.filter(
      (o) => o.price.currency?.toLowerCase() === currency
    );
    const scoped = inCurrency.length > 0 ? inCurrency : all;

    // One option per interval (first wins), ordered year -> month -> other.
    const byInterval = new Map<string, PlanOption>();
    scoped.forEach((o) => {
      const interval = o.price.recurring?.interval;
      if (interval && !byInterval.has(interval)) byInterval.set(interval, o);
    });
    return [...byInterval.values()].sort((a, b) => {
      const rank = (o: PlanOption) => {
        const i = INTERVAL_ORDER.indexOf(o.price.recurring?.interval ?? "");
        return i === -1 ? INTERVAL_ORDER.length : i;
      };
      return rank(a) - rank(b);
    });
  }, [products, permission, currency]);

  const yearly = planOptions.find((o) => o.price.recurring?.interval === "year");
  const monthly = planOptions.find(
    (o) => o.price.recurring?.interval === "month"
  );

  // Select the best-value (yearly) plan initially, and re-select when the
  // current choice drops out of the list (e.g. language/currency switch).
  useEffect(() => {
    if (planOptions.length === 0) return;
    const stillValid = planOptions.some((o) => o.price.id === selectedPriceId);
    if (!stillValid) {
      setSelectedPriceId((yearly ?? planOptions[0]).price.id);
    }
  }, [planOptions, yearly, selectedPriceId]);

  // `useActiveSubscription` resolves to `null` (not `undefined`) once it has
  // confirmed there is no subscription record at all for this permission -
  // as opposed to one that exists but is expiring/expired. Distinguishing
  // the two avoids telling a user who never subscribed that their "free
  // version" is ending. A logged-out visitor is the same case: the query is
  // disabled without auth, so it never resolves and stays `undefined` - treat
  // that as "no subscription" rather than falling through to the expiry copy.
  const neverSubscribed = !isAuthenticated || subscription === null;

  const daysLeft = daysUntilExpiry(subscription?.expiration);
  const expired = daysLeft !== null && daysLeft <= 0;

  // Only inside the final 30 days is there a countdown worth showing; before
  // that the subscription is simply running and the panel says nothing about
  // when it ends.
  const showExpiryCountdown =
    daysLeft !== null && isSubscriptionExpiryVisible(daysLeft);

  // A subscription that is still valid must be renewed/changed through the
  // Stripe billing portal, not through checkout: a fresh checkout always
  // creates a *second* paid subscription on the same customer and bills the
  // user twice. Once it has actually expired the portal can no longer
  // reactivate it, so checkout is the only way back — keep that path.
  // (`stripeCustomerId` is required to open the portal; without it fall back
  // to checkout, where there is no existing subscription to double up on.)
  const hasActiveSubscription =
    daysLeft !== null && daysLeft > 0 && !!user?.stripeCustomerId;

  // Active + set to renew: there is no "ending soon" to warn about, so the
  // left column drops the countdown/upsell copy in favour of a plain "you're
  // covered, it renews on its own" message.
  const autoRenewing = hasActiveSubscription && subscription?.will_renew !== false;

  // Not renewing, but the end is still far off: nothing is "ending soon", so
  // speak with the plain active-subscription copy instead of the countdown one.
  // Deliberately keyed off the expiry date alone (not `hasActiveSubscription`,
  // which also requires a Stripe customer) so a hand-granted subscription does
  // not get the "ending soon" title a year ahead of time.
  const activeButFarFromExpiry =
    daysLeft !== null && daysLeft > 0 && !showExpiryCountdown;

  const copy =
    LEFT_COLUMN_COPY[
      autoRenewing || activeButFarFromExpiry
        ? "active"
        : neverSubscribed
          ? "noSubscription"
          : expired
            ? "expired"
            : "ending"
    ];

  // Whole amounts render without decimals ("499 Kč", "€199"); fractional ones
  // keep cents ("€19.90", the /12 monthly-equivalent).
  const formatPrice = (amountMinor: number, priceCurrency: string) => {
    const value = amountMinor / 100;
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: priceCurrency.toUpperCase(),
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  };

  // Card title is the billing period, not the product name.
  const periodLabel = (price: StripePrice) => {
    const interval = price.recurring?.interval;
    if (interval === "year") return t("subscriptionUpgrade.yearly");
    if (interval === "month") return t("subscriptionUpgrade.monthly");
    return interval ?? "";
  };

  // % saved by paying yearly vs. 12x the monthly price.
  const annualSavingsPct =
    yearly && monthly
      ? Math.round(
          (1 - yearly.price.unit_amount / 12 / monthly.price.unit_amount) * 100
        )
      : null;

  // Checkout requires a signed-in customer; an unauthenticated visitor is sent
  // to sign in first instead, and back here afterwards to complete checkout.
  const handleContinue = async () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: location } });
      return;
    }
    if (!selectedPriceId) return;
    await createCheckoutSession([{ priceId: selectedPriceId, quantity: 1 }]);
  };

  return (
    <div className="mx-auto grid w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl md:grid-cols-2 dark:bg-gray-800">
      {/* Left: value proposition */}
      <div className="p-8">
        <h2 className="mb-4 text-2xl font-bold text-gray-900 dark:text-white">
          {t(`subscriptionUpgrade.${copy.title}`)}
        </h2>

        {!autoRenewing && showExpiryCountdown && (
          <span
            className={`mb-4 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${subscriptionExpiryTone(
              daysLeft
            )}`}
          >
            {isSubscriptionExpiryUrgent(daysLeft) && (
              <HiExclamation size={14} className="shrink-0" />
            )}
            {expired
              ? t("subscriptionUpgrade.expired")
              : t("subscriptionUpgrade.daysLeft", { count: daysLeft })}
          </span>
        )}

        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
          {t(`subscriptionUpgrade.${copy.intro}`)}
        </p>

        {/* Shown in every state, including an active subscription - there it
            reads as "what you're getting" rather than as an upsell. */}
        <p className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
          {t(`subscriptionUpgrade.${copy.benefitsHeading}`)}
        </p>
        <ul className="space-y-3">
          {copy.benefits.map((key) => (
            <li
              key={key}
              className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
            >
              <HiCheck size={18} className="mt-0.5 shrink-0 text-green-500" />
              <span>{t(`subscriptionUpgrade.${key}`)}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Right: plans */}
      <div className="relative bg-green-50 p-8 dark:bg-gray-900/40">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
            aria-label={t("common.close")}
          >
            <HiX size={20} />
          </button>
        )}

        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          {t("subscriptionUpgrade.optionsHeading")}
        </h3>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : hasActiveSubscription ? (
          <>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
              {t("subscriptionUpgrade.manageIntro")}
            </p>
            <button
              onClick={createCustomerPortalSession}
              disabled={customerPortalLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {customerPortalLoading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  {t("subscriptionUpgrade.manageSubscription")}
                  <span aria-hidden>→</span>
                </>
              )}
            </button>
          </>
        ) : planOptions.length === 0 ? (
          onClose ? (
            <button
              onClick={() => {
                onClose();
                navigate("/subscriptions");
              }}
              className="w-full rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700"
            >
              {t("subscriptionUpgrade.viewAllPlans")}
            </button>
          ) : (
            <p className="py-10 text-center text-sm text-gray-500">
              {t("subscriptions.noProducts")}
            </p>
          )
        ) : (
          <>
            <div className="space-y-3">
              {planOptions.map((option) => {
                const { price } = option;
                const selected = price.id === selectedPriceId;
                const isYearly = price.recurring?.interval === "year";
                return (
                  <button
                    key={price.id}
                    type="button"
                    onClick={() => setSelectedPriceId(price.id)}
                    className={`flex w-full items-start gap-3 rounded-xl border bg-white p-4 text-left transition-colors dark:bg-gray-800 ${
                      selected
                        ? "border-green-500 ring-1 ring-green-500"
                        : "border-gray-200 hover:border-green-300 dark:border-gray-700"
                    }`}
                  >
                    <span
                      className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                        selected ? "border-green-500" : "border-gray-400"
                      }`}
                    >
                      {selected && (
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                      )}
                    </span>
                    <span className="flex-1">
                      <span className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {periodLabel(price)}
                        </span>
                        {isYearly && annualSavingsPct && annualSavingsPct > 0 && (
                          <span className="text-xs font-medium text-orange-500">
                            {t("subscriptionUpgrade.saveBadge", {
                              percent: annualSavingsPct,
                            })}
                          </span>
                        )}
                      </span>
                      <span className="mt-1 flex items-baseline gap-2">
                        <span className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatPrice(price.unit_amount, price.currency)}
                        </span>
                        {isYearly && (
                          <span className="text-xs text-gray-500">
                            {t("subscriptionUpgrade.perMonthEquivalent", {
                              price: formatPrice(
                                price.unit_amount / 12,
                                price.currency
                              ),
                            })}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleContinue}
              disabled={checkoutLoading || (isAuthenticated && !selectedPriceId)}
              className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {checkoutLoading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  {t(
                    isAuthenticated
                      ? "subscriptionUpgrade.continue"
                      : "subscriptionUpgrade.signInFirst"
                  )}
                  <span aria-hidden>→</span>
                </>
              )}
            </button>

            {/* Expired subscription: the old invoices/receipts still live in
                the billing portal even though it can no longer reactivate a
                canceled plan, so offer it as a secondary action. */}
            {expired && !!user?.stripeCustomerId && (
              <button
                onClick={createCustomerPortalSession}
                disabled={customerPortalLoading}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-transparent dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {customerPortalLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <HiOutlineDocumentText size={16} />
                    {t("subscriptionUpgrade.checkInvoices")}
                    <span aria-hidden>→</span>
                  </>
                )}
              </button>
            )}

            <p className="mt-3 flex items-center justify-center gap-1 text-xs text-gray-500">
              <HiShieldCheck size={14} className="text-green-500" />
              {t("subscriptionUpgrade.securePayment")}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionUpgradePanel;
